import * as O from "fp-ts/lib/Option"
import * as FirebaseAuth from "firebase/auth"
import * as E from "fp-ts/lib/Either"
import {
  Variant,
  AgentFeedback,
  DealFeedback,
  Thread,
  ThreadComment,
  threadCategories,
} from "@flypengine/myFlyp-lib"
import { PageStatus } from "../../lib/types"
import * as A from "fp-ts/Array"
import * as R from "fp-ts/Record"
import { pipe } from "fp-ts/function"
import { parseHtml } from "../../lib/dom"

export type PageModel = {
  agentDealsModel: AgentDealsModel
  CSCalendarUrl: O.Option<string>
  modal: O.Option<Modal>
  lastFocused: O.Option<string>
}

export type Model = E.Either<PageStatus, PageModel>

export type Config<msg> = {
  user: FirebaseAuth.User
  propcode: string
  wrapperMsg: (m: Msg) => msg
  dealFeedbackMsg: () => msg
}

export const _ThreadModal = "ThreadModal"
export const _CalendlyModal = "CalendlyModal"

export type Modal =
  | Variant.Tagged<
      typeof _ThreadModal,
      ThreadWithMetadata & { brand: string; branch: O.Option<string> }
    >
  | Variant.Tagged<typeof _CalendlyModal, { calendarId: string }>

export const Modal = Variant.module<Modal>({
  ThreadModal: value => Variant.tagged(_ThreadModal, value),
  CalendlyModal: value => Variant.tagged(_CalendlyModal, value),
})

export const _init = "init"
export const _modalContents = "modalContents"
export const _lastFocused = "lastFocused"

export type Msg =
  | Variant.Tagged<typeof _init, void>
  | Variant.Tagged<typeof _modalContents, O.Option<Modal>>
  | Variant.Tagged<typeof _lastFocused, O.Option<string>>

export const Msg = Variant.module<Msg>({
  init: value => Variant.tagged(_init, value),
  modalContents: value => Variant.tagged(_modalContents, value),
  lastFocused: value => Variant.tagged(_lastFocused, value),
})

export type AgentDealsModel = {
  agentDeals: Array<
    Omit<AgentFeedback, "threads"> & {
      threads: ThreadWithMetadata[]
    }
  >
} & Pick<DealFeedback, "tags">

export type ThreadCommentParsed_Legacy = ThreadComment & {
  contents: O.Option<string>
}

export type ThreadWithMetadata = Omit<Thread, "threads"> & {
  categories: string[]
  lastComment: O.Option<ThreadCommentParsed_Legacy>
  comments: ThreadCommentParsed_Legacy[]
}

export const totalThreadsPerDeal = (data: AgentFeedback[]): number =>
  data.reduce((acc, agentDeal) => (acc = acc + agentDeal.threads.length), 0)

export const initFeedbackModel = (
  data: DealFeedback & {
    CSCalendarUrl: PageModel["CSCalendarUrl"]
  }
): Model =>
  E.of({
    modal: O.none,
    lastFocused: O.none,
    CSCalendarUrl: data.CSCalendarUrl,
    agentDealsModel: {
      tags: A.takeLeft(3)(data.tags),
      agentDeals: pipe(
        data.agentDeals,
        A.map(agentDeal => ({
          ...agentDeal,
          threads: pipe(
            agentDeal.threads,
            A.map(thread => ({
              ...thread,
              lastComment: pipe(
                thread.comments,
                A.head,
                O.map(comment => ({
                  ...comment,
                  contents: parseHtml(comment.html),
                }))
              ),
              categories: threadCategories(thread),
              comments: pipe(
                thread.comments,
                A.map(comment => ({
                  ...comment,
                  contents: parseHtml(comment.html),
                }))
              ),
            }))
          ),
        }))
      ),
    },
  })

export const commentsByDate = (
  data: ThreadCommentParsed_Legacy[]
): [string, ThreadCommentParsed_Legacy[]][] =>
  pipe(
    A.reduce(
      Object.assign({}),
      (
        acc: Record<string, ThreadCommentParsed_Legacy[]>,
        comment: ThreadCommentParsed_Legacy
      ) => {
        const currentDay = new Date(comment.createdAt).toDateString()
        return { ...acc, [currentDay]: [...(acc[currentDay] ?? []), comment] }
      }
    )(data),
    R.toEntries
  )
